import React, { useState, useEffect, useContext } from 'react';
import AuthContext from "../AuthContext";
import Loading from "../../components/Loading";
import {Snackbar} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import AffinityContext from "../AffinityContext";

const useStyles = makeStyles((theme) => ({
    alert: {
        backgroundColor: theme.palette.warning.dark
    },
}));

function withMultipleContexts(WrappedComponent, contextsConfig) {
    return function WrappedWithMultipleContexts(props) {
        const classes = useStyles();
        const [loadingPage, setLoadingPage] = useState(true);
        const [errorPage, setErrorPage] = useState(null);
        const [allContextData, setAllContextData] = useState({});
        const { authToken } = useContext(AuthContext);
        const {affinityId} = useContext(AffinityContext);
        const auth = () => {
            if (authToken) {
                return {authToken: authToken};
            } else {
                return {idAfinidad: affinityId};
            }
        }

        useEffect(() => {
            let isMounted = true;

            async function fetchData() {
                setLoadingPage(true);

                // Crear arrays para funciones de carga y setters
                const loadFunctions = contextsConfig.map(({ loadDataFunctions, storageKey }) => {
                    // Si hay datos en sessionStorage, los usamos
                    if (storageKey && sessionStorage.getItem(storageKey)) {
                        const storedValue = JSON.parse(sessionStorage.getItem(storageKey));
                        return Promise.resolve({ dataKey: storageKey, data: storedValue });
                    }

                    // Ejecutar todas las funciones de carga para este contexto
                    return Promise.all(loadDataFunctions.map(fn => fn(auth())))
                        .then(results => {
                            // Combinar los resultados en un solo objeto
                            const data = Object.assign({}, ...results);
                            // Guardar en sessionStorage si es necesario
                            if (storageKey) {
                                sessionStorage.setItem(storageKey, JSON.stringify(data));
                            }
                            return { dataKey: storageKey, data };
                        })
                        .catch(error => {
                            // Propagar el error con el dataKey para identificar el contexto
                            throw { dataKey: storageKey, error };
                        });
                });

                try {
                    // Ejecutar todas las cargas de datos (incluyendo los que vienen de sessionStorage)
                    const results = await Promise.allSettled(loadFunctions);

                    let errors = [];
                    let collectedData = {};

                    results.forEach((result, index) => {
                        const { dataKey } = contextsConfig[index];
                        if (result.status === 'fulfilled') {
                            collectedData[dataKey] = result.value.data;
                        } else {
                            errors.push({ dataKey: result.reason.dataKey, error: result.reason.error });
                            collectedData[dataKey] = null;
                        }
                    });

                    if (!isMounted) return;

                    if (errors.length > 0) {
                        const firstError = errors[0];
                        setErrorPage(`Error al cargar los datos de ${firstError.dataKey}`);
                    } else {
                        setAllContextData(collectedData);
                        setLoadingPage(false);
                    }
                } catch (error) {
                    if (!isMounted) return;
                    setErrorPage('Error al cargar los datos');
                }
            }

            fetchData();

            return () => {
                isMounted = false;
            };
        }, [authToken, affinityId]);

        if (loadingPage) return <Loading />;

        if (errorPage)
            return (
                <Snackbar open={true} autoHideDuration={8000} onClose={() => setErrorPage(null)}>
                    <Alert onClose={() => setErrorPage(null)} severity="warning" elevation={6} variant="filled"
                           classes={{ filledWarning: classes.alert }}
                    >
                        {errorPage}
                    </Alert>
                </Snackbar>
            );

        // Envolver el componente con todos los proveedores de contexto
        let content = <WrappedComponent {...props} />;
        contextsConfig.forEach(({ ContextProvider, dataKey }) => {
            const data = allContextData[dataKey];
            content = <ContextProvider initialData={data}>{content}</ContextProvider>;
        });

        return content;
    };
}

export default withMultipleContexts;